import { useFormikContext, useField } from 'formik';
import { isEqual } from 'lodash';
import { useMemo, useEffect, useCallback } from 'react';

import { AdditionalInformation } from '@hivebrite/design-system/AdditionalInformation';
import { Box } from '@hivebrite/design-system/Box';
import { SingleImageUpload } from '@hivebrite/design-system/SingleImageUpload';
import { useMatchBreakpoint } from '@hivebrite/design-system/Theme';
import { toast } from '@hivebrite/design-system/Toast';
import { usePrevious } from '@hivebrite/react-hooks';

import shouldDisplayError from 'hb-react/shared/components/formik/utils/shouldDisplayError';
import { useGlobalStorageFile } from 'hb-react/shared/hooks/useGlobalStorageV2';
import { FILES_VALIDATION_PROCESSING_FILES } from 'hb-react/shared/hooks/useGlobalStorageV2/constants';
import { formatAddedFileForSubmit } from 'hb-react/shared/hooks/useGlobalStorageV2/utils';
import { getFileValidator } from 'hb-react/shared/hooks/useGlobalStorageV2/utils/validators';
import useVirusScanningEnabled from 'hb-react/shared/hooks/useVirusScanningEnabled';
import {
  formatFileSize,
  fileSizeSI,
} from 'hb-react/shared/utils/files/fileSize';

interface AddCoverPictureV2Props {
  initialPreview?: string | null;
  name: string;
}

const AddCoverPictureV2 = ({
  name,
  initialPreview,
}: AddCoverPictureV2Props) => {
  const isMobile: boolean = useMatchBreakpoint(['s']);
  const { submitCount } = useFormikContext();

  const handleError = useCallback((gsFile) => {
    toast.error(gsFile.error);
  }, []);

  const maxFileSize = 50 * fileSizeSI.MB;
  const { file, updateFile } = useGlobalStorageFile({
    onError: handleError,
    config: {
      maxFileSize,
    },
  });
  const prevFile = usePrevious(file);
  const virusScanningEnabled = useVirusScanningEnabled();

  const [fieldInput, fieldMeta, fieldHelper] = useField({
    name,
    validate: (value) => value?.error,
  });

  const fileValidator = useMemo(
    () => getFileValidator(virusScanningEnabled),
    [virusScanningEnabled],
  );

  useEffect(() => {
    // Both are equal, no need to update
    if (isEqual(file, prevFile)) {
      return;
    }

    const error = fileValidator(file);
    const newValue = error ? { error } : formatAddedFileForSubmit(file);

    fieldHelper.setValue(newValue, true);
    fieldHelper.setTouched(true, false);
  }, [fieldHelper, file, fileValidator, prevFile]);

  const handleChange = (fileData: File | null) => {
    updateFile({ file: fileData });

    return true;
  };

  return (
    <Box display="block" mb={6}>
      <Box font="body-semibold-asphalt" mb={2}>
        {__('Cover picture')}
      </Box>
      <Box font="body-asphalt-italic" mb={2}>
        {__(
          'Recommended size is %{size} - The file must not exceed %{weight}',
        ).i__({
          size: '676x364px',
          weight: formatFileSize(maxFileSize),
        })}
      </Box>
      <SingleImageUpload
        ariaLabel={__('Cover Picture')}
        dataTestId={fieldInput.name}
        name={fieldInput.name}
        onChange={handleChange}
        width={isMobile ? '100%' : 338}
        height={182}
        initialPreview={initialPreview}
        isLoading={fieldMeta.error === FILES_VALIDATION_PROCESSING_FILES}
      />
      {shouldDisplayError({
        meta: fieldMeta,
        form: { submitCount },
      }) &&
        fieldMeta.error !== FILES_VALIDATION_PROCESSING_FILES &&
        fieldMeta.error && (
          <AdditionalInformation id={fieldInput.name}>
            {fieldMeta.error}
          </AdditionalInformation>
        )}
    </Box>
  );
};

export default AddCoverPictureV2;
